import { useRouter } from "next/router";
import { useEffect, useState } from "react";

/**
 * Extracts the search parameters from a URL or path.
 *
 * @param url - The URL from which to extract the search parameters.
 * @returns The URLSearchParams object containing the extracted search parameters.
 */
export function getSearchParams(url: string): URLSearchParams;

/**
 * Extracts the search parameters from a path.
 *
 * @param path - The path from which to extract the search parameters.
 * @returns The URLSearchParams object containing the extracted search parameters.
 */
export function getSearchParams(path: string): URLSearchParams;

export function getSearchParams(urlOrPath: string): URLSearchParams {
  // ^ = matches the beginning of the string
  // [^?]+ = matches any character that is not a question mark one or more times
  // (\?|$) = matches a question mark or the end of the string
  // eg. https://example.com?param=value -> ?param=value
  // eg. /signup?email=example@email.com -> ?email=example@email.com
  return new URLSearchParams(urlOrPath.replace(/^[^?]+(\?|$)/i, "?"));
}

/**
 * Builds a link to `path` that carries the current search params along.
 *
 * Not for an href on its own: on a statically rendered page the prerendered
 * html cannot contain the visitor's query, and react does not patch a
 * mismatched href while hydrating. Use `useLinkWithCurrentSearchParams`, which
 * separates the two renders (see `useCallCardLink` for the same problem).
 *
 * @param path - The path to link to.
 * @param currentUrlOrPath - The URL or path the user is on, eg. `router.asPath`.
 * @returns The path, with the current search params appended if there are any.
 */
export function withCurrentSearchParams(
  path: string,
  currentUrlOrPath: string,
): string {
  const search = getSearchParams(currentUrlOrPath).toString();
  return search ? `${path}?${search}` : path;
}

/**
 * A link to `path` that keeps the visitor's current search params.
 *
 * Login and sign-up are two views of the same step, and both read the url:
 * `redirectTo` decides where the visitor goes after authenticating, and `email`
 * prefills the form. Linking between them with a bare path drops that context,
 * so a host arriving from hosts.picter.com authenticates and lands on the
 * default page instead of back where they came from.
 *
 * The visitor's query is known only on the client. Both screens are statically
 * generated, so their html is built without it and is shared by every visitor —
 * and react does not patch a mismatched href while hydrating. The link
 * therefore carries the bare path through hydration and gains the query in a
 * re-render react does apply, the same split `useCallCardLink` makes.
 *
 * The cost is a narrow one: a click in the moment before hydration navigates
 * without the params, which is what every click does today.
 */
export function useLinkWithCurrentSearchParams(path: string): string {
  const { asPath } = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return mounted ? withCurrentSearchParams(path, asPath) : path;
}
